import React, { useState } from 'react';
import './EditSidebar.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import ProductDropdown from '../CreateSidebar/ProductDropdown';
import { USER_NAME } from '../../../constant';
import { useImage } from '../../../context/ImageContext';
import { sendToBackend } from '../../../utils/utilities';
import { uploadToS3 } from '../../../config/aws';
import { checkAvailableCredits } from '../../../dbApiFunctions';
import { showErrorAlert } from '../../../utils/sweetAlertConfig';
import '../../../styles/sweetAlert.css';
import CreativeControl from '../CreateSidebar/CreativeControl';

// Import sub-components
import ImageUploadSection from './ImageUploadSection';
import BrushSizeSection from './BrushSizeSection';
import TaskSection from './TaskSection';
import AccessoryUploadSection from './AccessoryUploadSection';
import PromptInput from '../CreateSidebar/PromptInput';
import GenerateButtonSection from './GenerateButtonSection';
import AddProductSection from './AddProductSection';
import ReColorSection from './ReColorSection';
import CollapsibleSection from './CollapsibleSection';

const EditSidebar = ({ editUploadedImages, setEditUploadedImages, brushSize, setBrushSize, eraserSize, setEraserSize, activeButton, setActiveButton, editEstTime, setEditEstTime, setJobResponseEdit, progressEdit, setCreditsAvailable, setSelectedEditGroup, selectedAction, setSelectedAction, selectedProduct, setSelectedProduct, inputValue, setInputValue, accessoryImage, setAccessoryImage, prompt, setPrompt, loadingCircle, setLoadingCircle, editLoadingCircle, setEditLoadingCircle, refNum, setRefNum, numberOfImages, setNumberOfImages, options, productPrompt, setProductPrompt, isRephrasing, setIsRephrasing, isRephraseClicked, setIsRephraseClicked, color, setColor, colorRefImage, setColorRefImage, reColorPrompt, setReColorPrompt, uploadedProductImage, setUploadedProductImage, openSection, setOpenSection
}) => {

    const [randomize, setRandomize] = useState(true);
    const [showRandomizeInfo, setShowRandomizeInfo] = useState(false);
    const [showAreaInfo, setShowAreaInfo] = useState(false);
    const [showActOnAreaInfo, setShowActOnAreaInfo] = useState(false);
    const [creativeControlValue, setCreativeControlValue] = useState(0);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [addFeatures, setAddFeatures] = useState(true);
    const [previousPromptValue, setPreviousPromptValue] = useState('');

    const [isPromptImageSelectorOpen, setIsPromptImageSelectorOpen] = useState(false);
    const { imageCanvasRef, drawCanvasRef } = useImage();

    const handleSectionToggle = (sectionId) => {
        if (sectionId === 'prompt') {
            if (openSection === 'prompt') {
                setPreviousPromptValue(prompt);
                setPrompt('');
            } else {
                setPrompt(previousPromptValue);
            }
        } else {
            setPrompt("")
        }
        setOpenSection(openSection === sectionId ? '' : sectionId);
    };


    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                showErrorAlert('Please select a valid image file.', 'Invalid File Type');
                return;
            }

            // Validate file size (max 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                showErrorAlert('File size must be less than 10MB.', 'File Too Large');
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                setEditUploadedImages([{ src: e.target.result }]);
                setSelectedEditGroup([{ src: e.target.result }]);
            };
            reader.onerror = () => {
                showErrorAlert('Error reading the file. Please try again.', 'File Read Error');
            };
            reader.readAsDataURL(file);
        }

        // Reset the input value to allow selecting the same file again
        event.target.value = '';
    };

    const handleProductImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setUploadedProductImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleColorRefUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setColorRefImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleProductSelect = (product) => {
        setSelectedProduct(product);
    };

    const handleGenerate = async () => {
        setLoadingCircle(true);
        try {
            const timestamp = new Date().getTime();
            const base_path = `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/`;

            if (selectedAction === 'add' && (!selectedProduct || selectedProduct.name === "No Product Selected") && (!accessoryImage) && (!prompt.trim()) && (!uploadedProductImage)) {
                showErrorAlert('Please select a product image or add Accessory Image or Enter a Prompt', 'Missing Information');
                return;
            }

            // Check if the selected action is 'add'            
            if (selectedAction === 'add') {
                if (accessoryImage) {
                    try {
                        // Convert base64 image to blob for upload
                        const base64Response = await fetch(accessoryImage);
                        const blob = await base64Response.blob();
                        await uploadToS3(base_path + 'accessory.png', blob.type, blob);
                        // console.log("handleGenerate = Uploaded accessory image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/accessory.png`);
                    } catch (error) {
                        console.error('Error uploading accessory image:', error);
                    }
                }

                // Upload uploaded product image if available
                if (uploadedProductImage) {
                    try {
                        // Convert base64 image to blob for upload
                        const base64Response = await fetch(uploadedProductImage);
                        const blob = await base64Response.blob();
                        await uploadToS3(base_path + 'uploadedProduct.png', blob.type, blob);
                        // console.log("handleGenerate = Uploaded uploaded product image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/uploadedProduct.png`);
                    } catch (error) {
                        console.error('Error uploading uploaded product image:', error);
                    }
                }

                // Upload selected product if available
                if (selectedProduct && selectedProduct.name !== "No Product Selected") {
                    try {
                        // If the product image is a URL, fetch it and upload it
                        const productResponse = await fetch(selectedProduct.src, {
                            mode: 'cors',
                            cache: 'no-cache',
                            headers: {
                                'Access-Control-Allow-Origin': '*'
                            }
                        });

                        if (!productResponse.ok) {
                            throw new Error(`Failed to fetch product image: ${productResponse.status} ${productResponse.statusText}`);
                        }

                        const productBlob = await productResponse.blob();
                        await uploadToS3(base_path + 'product.png', productBlob.type || 'image/png', productBlob);
                        // console.log("handleGenerate = Uploaded product image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/product.png`);
                    } catch (error) {
                        console.error('Error uploading product image:', error);
                        // Try an alternative approach with Image object
                        try {
                            const img = new Image();
                            img.crossOrigin = 'Anonymous';

                            // Create a promise to wait for the image to load
                            await new Promise((resolve, reject) => {
                                img.onload = resolve;
                                img.onerror = reject;
                                img.src = selectedProduct.src;
                            });

                            // Create a canvas to draw the image and get the data
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width;
                            canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(img, 0, 0);

                            // Convert canvas to blob
                            const canvasBlob = await new Promise(resolve => {
                                canvas.toBlob(blob => resolve(blob), 'image/png');
                            });

                            await uploadToS3(base_path + 'product.png', 'image/png', canvasBlob);
                            // console.log("handleGenerate = Uploaded product image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/product.png`);
                        } catch (altError) {
                            console.error('Alternative method-2 also failed:', altError);
                        }
                    }
                }
            }

            // Upload the masked image (if available)
            if (drawCanvasRef && drawCanvasRef.current) {
                try {
                    const drawCanvas = drawCanvasRef.current;
                    // Convert canvas to blob
                    const maskBlob = await new Promise(resolve => {
                        drawCanvas.toBlob(blob => {
                            resolve(blob);
                        }, 'image/png');
                    });

                    if (maskBlob) {
                        await uploadToS3(base_path + 'mask.png', 'image/png', maskBlob);
                        // console.log("handleGenerate = Uploaded mask image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/mask.png`);
                    }
                } catch (error) {
                    console.error('Error uploading mask image:', error);
                }
            }
            setEditLoadingCircle(true);

            // Upload the main image if available
            if (editUploadedImages && editUploadedImages.length > 0 && editUploadedImages[0].src) {
                try {
                    // For data URLs, use direct base64 conversion
                    if (editUploadedImages[0].src.startsWith('data:')) {
                        try {
                            const base64Data = editUploadedImages[0].src.split(',')[1];
                            const binaryData = atob(base64Data);
                            const arrayBuffer = new ArrayBuffer(binaryData.length);
                            const uint8Array = new Uint8Array(arrayBuffer);

                            for (let i = 0; i < binaryData.length; i++) {
                                uint8Array[i] = binaryData.charCodeAt(i);
                            }

                            const blob = new Blob([arrayBuffer], { type: 'image/png' });
                            await uploadToS3(base_path + 'original.png', 'image/png', blob);
                            // console.log("handleGenerate = Uploaded original image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/original.png`);
                        } catch (dataUrlError) {
                            console.error('Error processing data URL:', dataUrlError);
                        }
                    } else {
                        // Try to fetch the URL
                        const imgResponse = await fetch(editUploadedImages[0].src, {
                            mode: 'cors',
                            cache: 'no-cache',
                            headers: {
                                'Access-Control-Allow-Origin': '*'
                            }
                        });

                        if (!imgResponse.ok) {
                            throw new Error(`Failed to fetch input image: ${imgResponse.status} ${imgResponse.statusText}`);
                        }

                        const imgBlob = await imgResponse.blob();
                        await uploadToS3(base_path + 'original.png', imgBlob.type || 'image/png', imgBlob);
                        // console.log("handleGenerate = Uploaded original image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/original.png`);
                    }
                } catch (error) {
                    console.error('Error uploading input image:', error);

                    // Try with Canvas approach
                    try {
                        const img = new Image();
                        img.crossOrigin = 'Anonymous';

                        // Create a promise to wait for the image to load
                        await new Promise((resolve, reject) => {
                            img.onload = resolve;
                            img.onerror = reject;
                            if (editUploadedImages[0].src.startsWith('data:')) {
                                img.src = editUploadedImages[0].src;
                            } else {
                                // Add cache buster for external URLs
                                img.src = `${editUploadedImages[0].src}${editUploadedImages[0].src.includes('?') ? '&' : '?'}t=${Date.now()}`;
                            }
                        });

                        // Create a canvas to draw the image and get the data
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);

                        // Try direct upload of canvas blob
                        const canvasBlob = await new Promise(resolve => {
                            canvas.toBlob(blob => resolve(blob), 'image/png');
                        });

                        await uploadToS3(base_path + 'original.png', 'image/png', canvasBlob);
                        // console.log("handleGenerate = Uploaded original image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/original.png`);
                    } catch (canvasError) {
                        console.error('Canvas method also failed:', canvasError);

                        // Last resort - try to take a screenshot of the image element
                        try {
                            // Find the actual image element in the DOM
                            const imgElement = document.querySelector('.image-section img');
                            if (imgElement) {
                                // Create a new canvas with the exact dimensions of the image
                                const finalCanvas = document.createElement('canvas');
                                finalCanvas.width = imgElement.naturalWidth || imgElement.width;
                                finalCanvas.height = imgElement.naturalHeight || imgElement.height;

                                // Draw the image onto the canvas
                                const finalCtx = finalCanvas.getContext('2d');
                                finalCtx.drawImage(imgElement, 0, 0, finalCanvas.width, finalCanvas.height);

                                // Get the canvas data as a blob
                                const finalBlob = await new Promise(resolve => {
                                    finalCanvas.toBlob(blob => resolve(blob), 'image/png', 1.0);
                                });

                                await uploadToS3(base_path + 'original.png', 'image/png', finalBlob);
                                // console.log("handleGenerate = Uploaded original image to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/inputFolder/original.png`);
                            } else {
                                console.error('Could not find image element in DOM');
                            }
                        } catch (finalError) {
                            console.error('All methods failed to upload input image:', finalError);
                        }
                    }
                }
            }

            // Upload metadata
            const metadata = {
                productName: selectedProduct.name === "No Product Selected" ? null : selectedProduct.name,
                prompt: prompt,
                // reColorPrompt: reColorPrompt,
                totalImages: numberOfImages,
                // accessoryImage: accessoryImage ? true : false,
                uploadedProductImage: uploadedProductImage ? true : false,
                timestamp: timestamp,
                userName: USER_NAME,
                // act_on_area: selectedAction,
                // randomize: randomize,
                // creativeControl: options[creativeControlValue].label,
                // creativeControlValue: creativeControlValue,
                time: new Date().toLocaleString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    month: 'short',
                    day: 'numeric',
                    hour12: false
                })
            };

            try {
                await uploadToS3(`USERS/${USER_NAME}/all_editPage/${timestamp}/metadata.json`, 'application/json', JSON.stringify(metadata));
                // console.log("handleGenerate = Uploaded metadata to S3 at location ", `USERS/${USER_NAME}/all_editPage/${timestamp}/metadata.json`);
            } catch (error) {
                console.error('Error uploading metadata:', error);
            }

            // Send data to backend for processing
            try {
                const password = sessionStorage.getItem("password");
                const email = sessionStorage.getItem("email");
                const creditsAvailable = await checkAvailableCredits(email, password, numberOfImages);
                if (!creditsAvailable.status) {
                    showErrorAlert(creditsAvailable.message, 'Insufficient Credits');
                    return;
                }
                const backendPromises = [];
                if (numberOfImages > 1) {
                    const metadata2 = structuredClone(metadata);
                    metadata2['pid'] = numberOfImages - 2;
                    backendPromises.push(sendToBackend('edit', metadata2, 0, setCreditsAvailable));
                }

                if (numberOfImages > 2) {
                    const metadata3 = structuredClone(metadata);
                    metadata3['pid'] = numberOfImages - 3;
                    backendPromises.push(sendToBackend('edit', metadata3, 0, setCreditsAvailable));
                }

                if (numberOfImages > 3) {
                    const metadata4 = structuredClone(metadata);
                    metadata4['pid'] = numberOfImages - 4;
                    backendPromises.push(sendToBackend('edit', metadata4, 0, setCreditsAvailable));
                }

                metadata['pid'] = numberOfImages - 1;
                backendPromises.push(sendToBackend('edit', metadata, numberOfImages, setCreditsAvailable));

                // Wait for all backend calls to complete
                const responses = await Promise.all(backendPromises);

                const response = responses[responses.length - 1]; // Get the last response

                setJobResponseEdit(response);
                if (response && response.hasOwnProperty('est_time')) {
                    // console.log("Edit waiting time is=", response.est_time);
                }
            } catch (error) {
                console.error("Error in handleGenerate:", error);
                showErrorAlert("Failed to generate images. Please try again.")
            }

        } catch (error) {
            console.error('Error in handleGenerate:', error);
            showErrorAlert("There was an error processing the images. Please try again.");
        } finally {
            setEditEstTime(3);
        }
        setLoadingCircle(false);
        setEditLoadingCircle(false);
    };

    return (
        <>
            <div className={`fixed inset-0 backdrop-blur-lg transition-all duration-500 opacity-0 pointer-events-none`}></div>
            <div className="relative">
                <div className="fixed top-0 left-0 h-screen w-full lg:w-1/4 bg-white flex flex-col">
                    <div className="p-4 lg:p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-800">Edit</h2>
                    </div>
                    <div
                        className="flex-1 overflow-y-auto p-4 lg:p-6"
                        onMouseEnter={(e) => {
                            document.body.style.overflow = 'hidden';
                        }}
                        onMouseLeave={(e) => {
                            document.body.style.overflow = '';
                        }}
                    >
                        <div className="flex flex-col pt-8">
                            {/* Image Section */}
                            <ImageUploadSection
                                editUploadedImages={editUploadedImages}
                                handleFileUpload={handleFileUpload}
                            />
                            <div style={{ height: 16 }} /> {/* Reduced from 24 */}

                            {/* Prompt Section */}
                            <CollapsibleSection
                                title="Edit with Text "
                                isOpen={openSection === 'prompt'}
                                onToggle={handleSectionToggle}
                                sectionId="prompt"
                                infoText="Add, Remove or Modify just with text"
                            >
                                <PromptInput
                                    productPrompt=""
                                    refImagePrompt=""
                                    prompt={prompt}
                                    setPrompt={setPrompt}
                                    isRephrasing={isRephrasing}
                                    setIsRephrasing={setIsRephrasing}
                                    setIsPromptImageSelectorOpen={setIsPromptImageSelectorOpen}
                                    setIsRephraseClicked={setIsRephraseClicked}
                                    repharseAllowed={false}
                                    promptPlaceholder={`In natural language describe what would you like to change.
Tip: Only make 1 change at a time.`}
                                />
                            </CollapsibleSection>

                            {/* Add Product Section */}
                            <AddProductSection
                                brushSize={brushSize}
                                setBrushSize={setBrushSize}
                                eraserSize={eraserSize}
                                setEraserSize={setEraserSize}
                                activeTool={activeButton}
                                setActiveTool={setActiveButton}
                                handleFileUpload={handleProductImageUpload}
                                setUploadedImage={setUploadedProductImage}
                                uploadedImage={uploadedProductImage}
                                selectedProduct={selectedProduct}
                                setSelectedProduct={setSelectedProduct}
                                setRefNum={setRefNum}
                                setCreditsAvailable={setCreditsAvailable}
                                handleProductSelect={handleProductSelect}
                                isOpen={openSection === 'addProduct'}
                                onToggle={handleSectionToggle}
                                sectionId="addProduct"
                                setProductPrompt={setProductPrompt}
                            />

                            {/* Re-Color Section */}
                            <ReColorSection
                                color={color}
                                setColor={setColor}
                                colorRefImage={colorRefImage}
                                handleColorRefUpload={handleColorRefUpload}
                                prompt={reColorPrompt}
                                setPrompt={setReColorPrompt}
                                isOpen={openSection === 'reColor'}
                                onToggle={handleSectionToggle}
                                sectionId="reColor"
                            />

                            {/* Generate Button Section */}
                            <GenerateButtonSection
                                numberOfImages={numberOfImages}
                                setNumberOfImages={setNumberOfImages}
                                handleGenerate={handleGenerate}
                                loadingCircle={loadingCircle}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EditSidebar;